import { extractColors } from 'extract-colors';
import { generatePath } from 'react-router';

import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { Route as Routes } from 'src/shared/route';
import { request } from 'src/shared/utils/request';

export const darkenHexColor = (hex: string, factor: number): string => {
  const hexStr = hex.replace(/^#/, '');

  const red = parseInt(hexStr.substring(0, 2), 16);
  const green = parseInt(hexStr.substring(2, 4), 16);
  const blue = parseInt(hexStr.substring(4, 6), 16);

  const darkenedRed = Math.round(red * factor);
  const darkenedGreen = Math.round(green * factor);
  const darkenedBlue = Math.round(blue * factor);

  const rHex = darkenedRed.toString(16).padStart(2, '0');
  const gHex = darkenedGreen.toString(16).padStart(2, '0');
  const bHex = darkenedBlue.toString(16).padStart(2, '0');

  return `#${rHex}${gHex}${bHex}`;
};

const MIDDLE_COLOR_LEVEL = 90;

const normalizeColor = (color: number, averageLevel: number) => {
  let normalizedColor =
    averageLevel < MIDDLE_COLOR_LEVEL
      ? color + (MIDDLE_COLOR_LEVEL - averageLevel)
      : color - (averageLevel - MIDDLE_COLOR_LEVEL);

  if (normalizedColor < 0) {
    normalizedColor = 0;
  } else if (normalizedColor > 255) {
    normalizedColor = 255;
  }

  return Math.round(normalizedColor);
};

export const normalizeHexColor = (hex: string): string => {
  const hexStr = hex.replace(/^#/, '');

  const red = parseInt(hexStr.substring(0, 2), 16);
  const green = parseInt(hexStr.substring(2, 4), 16);
  const blue = parseInt(hexStr.substring(4, 6), 16);

  const averageLevel = (red + green + blue) / 3;

  const normalizedRed = normalizeColor(red, averageLevel);
  const normalizedGreen = normalizeColor(green, averageLevel);
  const normalizedBlue = normalizeColor(blue, averageLevel);

  const rHex = normalizedRed.toString(16).padStart(2, '0');
  const gHex = normalizedGreen.toString(16).padStart(2, '0');
  const bHex = normalizedBlue.toString(16).padStart(2, '0');

  return `#${rHex}${gHex}${bHex}`;
};

export const normalizeHexColorFromSrc = async (src: string) => {
  const normalizedColor = await request(src)
    .then(r => r.blob())
    .then(blob => URL.createObjectURL(blob))
    .then(extractColors)
    .then(colors => {
      const hexColor = colors.sort((a, b) => b.area - a.area)[0].hex;
      return normalizeHexColor(hexColor);
    });
  return normalizedColor;
};

export const getStylesRoute = (image: AiDisplayImage | undefined) => {
  if (!image) {
    return '';
  }

  return generatePath(Routes.HOMEOWNER_APP_ORIGINAL.path, {
    aiOriginalImageId: image.aiOriginalImageId,
  });
};
